import React, { useCallback, useEffect, useState } from 'react'
import { Modal, Button, Table } from 'react-bootstrap'
import Select from 'react-select'
import Swal from 'sweetalert2'
import Spinner from 'react-bootstrap/Spinner'
import { getRequestOptions } from '../helpers/Fetchwrapper'
import { formatDecimalQuantity, numberFormat } from '../helpers/GlobalHelper'

interface PackingBomOption {
	label: string;
	value: number;
}

interface RateLine {
	idFormulationItems: number;
	itemName: string;
	categoryName: string;
	unitName: string;
	percentage?: number;
	quantity: number;
	rate: number;
	amount: number;
}

interface RateData {
	product: {
		idProducts: number;
		productName: string;
		productCode: string;
		packing: string;
		size: number;
		unitName: string;
	};
	packingBoms: { idPackingBoms: number; bomName: string }[];
	selectedPackingBomId: number;
	selectedBomName: string;
	effectiveDate: string;
	itemLines: RateLine[];
	packingLines: RateLine[];
	itemSubtotal: number;
	packingSubtotal: number;
	grandTotal: number;
}

interface PackingBomViewRateModalProps {
	show: boolean;
	handleClose: () => void;
	product: {
		idProducts: number;
		productName: string;
		packing?: string;
		size?: number | string;
		unitName?: string;
	} | null;
	defaultPackingBomId?: number | null;
}

const showMissingRateError = (resp: any) => {
	const missingNames = (resp.missingItems || []).map((item: any) => item.itemName).join('<br>')
	Swal.fire({
		icon: 'error',
		title: 'Average Rate Missing',
		html: missingNames
			? `Average rate not found for the following items as of today:<br><br>${missingNames}`
			: (resp.message || 'Average rate not found for one or more items.'),
	})
}

const PackingBomViewRateModal: React.FC<PackingBomViewRateModalProps> = ({
	show,
	handleClose,
	product,
	defaultPackingBomId = null,
}) => {
	const [loading, setLoading] = useState(false)
	const [rateData, setRateData] = useState<RateData | null>(null)
	const [bomOptions, setBomOptions] = useState<PackingBomOption[]>([])
	const [selectedBom, setSelectedBom] = useState<PackingBomOption | null>(null)

	const fetchRates = useCallback(async (idProducts: number, idPackingBom?: number | null) => {
		setLoading(true)
		setRateData(null)
		try {
			let url = `${process.env.REACT_APP_API_URL}packing-boms/${idProducts}/rates`
			if (idPackingBom) {
				url += `?id_packing_bom=${idPackingBom}`
			}
			const response = await fetch(url, getRequestOptions())
			const resp = await response.json()

			if (!resp.success) {
				showMissingRateError(resp)
				return
			}

			const data: RateData = resp.data
			setRateData(data)

			const options: PackingBomOption[] = (data.packingBoms || []).map((bom) => ({
				label: bom.bomName,
				value: bom.idPackingBoms,
			}))
			setBomOptions(options)

			const selected = options.find((option) => option.value === data.selectedPackingBomId) || options[0] || null
			setSelectedBom(selected)
		} catch (error) {
			console.log(error)
			Swal.fire({ icon: 'error', title: 'Error', text: 'Could not load rate data.' })
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		if (show && product?.idProducts) {
			fetchRates(product.idProducts, defaultPackingBomId)
		}
		if (!show) {
			setRateData(null)
			setBomOptions([])
			setSelectedBom(null)
		}
	}, [show, product?.idProducts, defaultPackingBomId, fetchRates])

	const handleBomChange = (option: PackingBomOption | null) => {
		if (!option || !product?.idProducts) {
			return
		}
		setSelectedBom(option)
		fetchRates(product.idProducts, option.value)
	}

	const displayProduct = rateData?.product || product

	return (
		<Modal show={show} onHide={handleClose} size="xl" centered scrollable>
			<Modal.Header closeButton>
				<Modal.Title>
					View Rate {displayProduct?.productName ? `- ${displayProduct.productName}` : ''}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{displayProduct && (
					<div className="mb-4">
						<div className="row">
							<div className="col-md-4">
								<strong>Packing:</strong> {displayProduct.packing || '-'}
							</div>
							<div className="col-md-4">
								<strong>Size:</strong> {displayProduct.size} {displayProduct.unitName || ''}
							</div>
							{rateData?.effectiveDate && (
								<div className="col-md-4">
									<strong>Rate as of:</strong> {rateData.effectiveDate}
								</div>
							)}
						</div>
					</div>
				)}

				{bomOptions.length > 0 && (
					<div className="mb-4" style={{ maxWidth: '320px' }}>
						<label className="form-label fw-bold">Packing BOM</label>
						<Select
							value={selectedBom}
							onChange={(option) => handleBomChange(option as PackingBomOption | null)}
							options={bomOptions}
							isDisabled={loading}
						/>
					</div>
				)}

				{loading && (
					<div className="text-center py-5">
						<Spinner animation="border" role="status" />
					</div>
				)}

				{!loading && rateData && (
					<>
						<h5 className="mb-3">Item BOM (Blending)</h5>
						<Table bordered striped size="sm" responsive className="mb-4">
							<thead>
								<tr>
									<th>Item</th>
									<th>Category</th>
									<th>Unit</th>
									<th className="text-end">%</th>
									<th className="text-end">Qty</th>
									<th className="text-end">Rate</th>
									<th className="text-end">Amount</th>
								</tr>
							</thead>
							<tbody>
								{rateData.itemLines.length === 0 ? (
									<tr>
										<td colSpan={7} className="text-center text-muted">No item BOM formulations found.</td>
									</tr>
								) : (
									rateData.itemLines.map((line) => (
										<tr key={`item-${line.idFormulationItems}`}>
											<td>{line.itemName}</td>
											<td>{line.categoryName}</td>
											<td>{line.unitName}</td>
											<td className="text-end">{formatDecimalQuantity(line.percentage, 4)}</td>
											<td className="text-end">{formatDecimalQuantity(line.quantity)}</td>
											<td className="text-end">{numberFormat(line.rate)}</td>
											<td className="text-end">{numberFormat(line.amount)}</td>
										</tr>
									))
								)}
							</tbody>
							{rateData.itemLines.length > 0 && (
								<tfoot>
									<tr>
										<th colSpan={6} className="text-end">Subtotal</th>
										<th className="text-end">{numberFormat(rateData.itemSubtotal)}</th>
									</tr>
								</tfoot>
							)}
						</Table>

						<h5 className="mb-3">Packing BOM</h5>
						<Table bordered striped size="sm" responsive className="mb-4">
							<thead>
								<tr>
									<th>Item</th>
									<th>Category</th>
									<th>Unit</th>
									<th className="text-end">Qty</th>
									<th className="text-end">Rate</th>
									<th className="text-end">Amount</th>
								</tr>
							</thead>
							<tbody>
								{rateData.packingLines.length === 0 ? (
									<tr>
										<td colSpan={6} className="text-center text-muted">No packing BOM formulations found.</td>
									</tr>
								) : (
									rateData.packingLines.map((line) => (
										<tr key={`packing-${line.idFormulationItems}`}>
											<td>{line.itemName}</td>
											<td>{line.categoryName}</td>
											<td>{line.unitName}</td>
											<td className="text-end">{formatDecimalQuantity(line.quantity)}</td>
											<td className="text-end">{numberFormat(line.rate)}</td>
											<td className="text-end">{numberFormat(line.amount)}</td>
										</tr>
									))
								)}
							</tbody>
							{rateData.packingLines.length > 0 && (
								<tfoot>
									<tr>
										<th colSpan={5} className="text-end">Subtotal</th>
										<th className="text-end">{numberFormat(rateData.packingSubtotal)}</th>
									</tr>
								</tfoot>
							)}
						</Table>

						<div className="text-end">
							<h5 className="mb-0">
								Grand Total: <span className="text-primary">{numberFormat(rateData.grandTotal)}</span>
							</h5>
						</div>
					</>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" size="sm" onClick={handleClose}>
					Close
				</Button>
			</Modal.Footer>
		</Modal>
	)
}

export default PackingBomViewRateModal
